import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "search",
        "results",
        "skeleton"
    ]

    connect() {
        console.log("Search controller connected")
        // Use lastSent and lastReceived to ignore responses that arrive out-of-order.
        this.lastSent = 0;
        this.lastReceived = 0;

        // A lone text input makes Enter submit the surrounding form. On the order
        // forms that posted the order straight past the Save button, skipping the
        // trucking-options check. Prevent the native submit but trigger the search.
        this.blockEnter = (event) => {
            if (event.key === "Enter") {
                event.preventDefault()
                this.concurrentSearch()
            }
        }
        if (this.hasSearchTarget) this.searchTarget.addEventListener("keydown", this.blockEnter)
    }

    disconnect() {
        this.stopPrevious()
        if (this.hasSearchTarget && this.blockEnter) {
            this.searchTarget.removeEventListener("keydown", this.blockEnter)
        }
    }

    search() {
        let query = this.searchTarget.value

        this.debounce(query)
    }

    showSkeleton() {
        if (!this.hasSkeletonTarget || !this.hasResultsTarget) return

        this.resultsTarget.innerHTML = this.skeletonTarget.innerHTML

        const query = this.searchTarget.value.trim()
        const label = this.resultsTarget.querySelector("[data-search-label]")
        const term = this.resultsTarget.querySelector("[data-search-query]")
        if (!label || !term) return

        label.textContent = query ? "Searching for " : "Searching"
        term.textContent = query ? `“${query}”` : ""
    }

    clear() {
        this.stopPrevious()
        this.searchTarget.value = ""
    }

    concurrentSearch() {
        let query = this.searchTarget.value
        this.fetchResults(query)
    }

    searchingResults() {
        this.resultsTarget.innerHTML = `
            <div class="ice:gap-3 ice:flex ice:justify-center ice:items-center ice:text-3xl" >
                <i class="fa fa-spinner fa-spin"></i>
                <span>Fetching results...</span>
            </div>`
    }

    debounce(query) {
        let delay = parseInt(this.data.get("debounce")) || 400
        this.stopPrevious()

        // console.log(`Starting search debounce with delay of ${delay}`)
        this.debouncer = setInterval((query) => {
            this.fetchResults(query)
            this.stopPrevious()
        }, delay)
    }

    stopPrevious() {
        if (this.debouncer) {
            // console.log("Stopping search debounce")
            clearInterval(this.debouncer)
        }
    }

    fetchResults(query) {
        // At the request, not at the keystroke: with a debounce in front, showing it
        // on every keypress flashes the skeleton in and out while someone types a
        // part number.
        this.showSkeleton()

        // console.log("Searching for: ", this.searchTarget.value)
        let encodedQuery = encodeURIComponent(this.searchTarget.value)
        // let encodedAuthenticityToken = this.authenticityToken()
        // The url may already carry a param — the duplicate picker passes
        // duplicate_from — so join with & rather than a second ?.
        const base = this.data.get("url")
        let url = `${base}${base.includes("?") ? "&" : "?"}query=${encodedQuery}`
        // console.log(this.data)
        // console.log("At this URL: ", url)

        // The responses don't always arrive in the same order we sent them,
        // which causes the search results to "revert" to something you asked for before.
        // We use currentFetch to ignore all but the most-recently-sent request.
        const thisFetch = this.lastSent += 1;

        fetch(url, {
            credentials: "same-origin",
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            }
        })
            .then(response => {
                // console.log("Got search results", response)
                return response
            })
            .then(response => response.text())
            .then(html => {
                if (thisFetch <= this.lastReceived) return;
                this.lastReceived = thisFetch;

                // console.log("Adding search results to DOM", html)
                this.resultsTarget.innerHTML = html
                // Raise an event so that other things can respond,
                // e.g. toggle needs to set the new elements to the same show/hide as the existing ones.
                window.dispatchEvent(new CustomEvent("searched"));
            })
    }
}
